import { Controller } from "@hotwired/stimulus"

// In-memory "Customize view" for the account-health dashboard: show/hide whole metric
// sections and individual cards within them. State is client-side only (localStorage, keyed
// per account so each client keeps its own layout) and survives reload — no backend. The menu
// is built from the sections/cards present on the current tab, so it always reflects what's
// on screen. Cards carry data-section linking them to their parent section for the nesting.
export default class extends Controller {
  static targets = ["section", "card", "list", "menu"]
  static values = { storageKey: String }

  connect() {
    this.hidden = new Set(this.load())
    this.apply()
    this.buildList()
  }

  toggleMenu() {
    this.menuTarget.classList.toggle("hidden")
  }

  toggle(event) {
    const { key } = event.currentTarget.dataset
    if (event.currentTarget.checked) this.hidden.delete(key)
    else this.hidden.add(key)
    this.save()
    this.apply()
  }

  reset() {
    this.hidden.clear()
    this.save()
    this.apply()
    this.buildList()
  }

  apply() {
    const toggle = (el) => el.classList.toggle("hidden", this.hidden.has(el.dataset.key))
    this.sectionTargets.forEach(toggle)
    this.cardTargets.forEach(toggle)
  }

  buildList() {
    this.listTarget.innerHTML = ""

    // Cards grouped by their parent section key (preserving DOM order).
    const cardsBySection = {}
    this.cardTargets.forEach((el) => {
      const parent = el.dataset.section
      ;(cardsBySection[parent] ||= []).push(el)
    })

    const seen = new Set()
    this.sectionTargets.forEach((section) => {
      const { key } = section.dataset
      if (seen.has(key)) return // a section can appear once per tab; list it once
      seen.add(key)

      this.listTarget.append(this.row(key, section.dataset.label, false))
      ;(cardsBySection[key] || []).forEach((card) => {
        this.listTarget.append(this.row(card.dataset.key, card.dataset.label, true))
      })
    })
  }

  row(key, label, indented) {
    const row = document.createElement("label")
    row.className = `flex items-center gap-2 text-sm cursor-pointer py-1 ${indented ? "pl-5 text-sg-gray-600" : "text-sg-gray-800 font-medium"}`
    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.className = "checkbox"
    checkbox.checked = !this.hidden.has(key)
    checkbox.dataset.key = key
    checkbox.dataset.action = "account-health--customize#toggle"
    const text = document.createElement("span")
    text.textContent = label || key
    row.append(checkbox, text)
    return row
  }

  load() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKeyValue)) || []
    } catch {
      return []
    }
  }

  save() {
    localStorage.setItem(this.storageKeyValue, JSON.stringify([...this.hidden]))
  }
}
